import { FC, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import PasswordConfirmModal from './PasswordConfirmModal'
import useAuth from '../../../../hooks/useAuth'
import {
  authorizeSellerQueueAction,
  filterAdminQueueSellerOrders,
  getUsersByTypeRoute,
  hasTotvsIntegrationForConference,
} from '../services'
import { ConferenceOrderItem, ConferenceSeller } from '../types'

interface ConferenceTabProps {
  isPrivileged: boolean
}

const normalizeCpf = (value: string): string => value.replace(/\D/g, '')

const normalizeCode = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const toObject = (value: unknown): Record<string, unknown> => {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }

  if (typeof value === 'object') {
    return value as Record<string, unknown>
  }

  return {}
}

const pickContentValue = (content: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const raw = content[key]
    if (typeof raw === 'string' && raw.trim()) {
      return raw
    }
  }

  return ''
}

const extractSellerFields = (order: ConferenceOrderItem): { cpf: string; code: string } => {
  const content = toObject(order.content)
  return {
    cpf: pickContentValue(content, ['sellerCpf', 'cpfSeller', 'sellerCpfCnpj', 'cpfVendedor']),
    code: pickContentValue(content, ['codeSeller', 'sellerCode', 'codSeller']),
  }
}

const toUtcStartDate = (dateValue: string): string => `${dateValue}T00:00:00.000Z`
const toUtcEndDate = (dateValue: string): string => `${dateValue}T23:59:59.999Z`

const formatOrderTotal = (total?: number | null): string => {
  if (typeof total !== 'number' || Number.isNaN(total)) {
    return '-'
  }

  // In this conference flow, Ninegrid returns `total` in cents.
  const totalInReais = total / 100

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalInReais)
}

export const ConferenceTab: FC<ConferenceTabProps> = ({ isPrivileged }) => {
  const { loginInfo } = useAuth()
  const pageSize = 20
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState<ConferenceOrderItem[]>([])
  const [sellers, setSellers] = useState<ConferenceSeller[]>([])
  const [startDate, setStartDate] = useState('')
  const [finalDate, setFinalDate] = useState('')
  const [orderIdSearch, setOrderIdSearch] = useState('')
  const [orderExternalSearch, setOrderExternalSearch] = useState('')
  const [customerNameSearch, setCustomerNameSearch] = useState('')
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [checkingTotvsIntegration, setCheckingTotvsIntegration] = useState(false)
  const [hasTotvsIntegration, setHasTotvsIntegration] = useState(false)

  const currentUserId = Number(loginInfo?.id) || 0
  const currentUserProfile = String(loginInfo?.profile || '').toLowerCase()
  const isSellerProfile = currentUserProfile === 'seller'

  useEffect(() => {
    const today = new Date()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    setStartDate(yesterday.toISOString().split('T')[0])
    setFinalDate(today.toISOString().split('T')[0])
  }, [])

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const result = await getUsersByTypeRoute('seller')
        if (isPrivileged) {
          setSellers(result)
          return
        }

        const scopedSellers = result.filter((seller) => seller.id === currentUserId)
        setSellers(scopedSellers)
        if (scopedSellers.length === 1) {
          setSelectedSellerId(scopedSellers[0].id)
        }
      } catch (error) {
        Swal.fire('Erro', 'Não foi possível carregar vendedores da loja.', 'error')
      }
    }

    loadSellers()
  }, [currentUserId, isPrivileged])

  useEffect(() => {
    const checkTotvsIntegration = async () => {
      setCheckingTotvsIntegration(true)
      try {
        const hasIntegration = await hasTotvsIntegrationForConference()
        setHasTotvsIntegration(hasIntegration)
      } finally {
        setCheckingTotvsIntegration(false)
      }
    }

    checkTotvsIntegration()
  }, [])

  const selectedSeller = useMemo(
    () => sellers.find((seller) => seller.id === selectedSellerId) || null,
    [sellers, selectedSellerId]
  )

  const validateFilter = (): boolean => {
    if (!startDate || !finalDate) {
      Swal.fire('Atenção', 'Preencha Data Início e Data Final.', 'warning')
      return false
    }

    if (new Date(startDate) > new Date(finalDate)) {
      Swal.fire('Atenção', 'Data Início não pode ser maior que Data Final.', 'warning')
      return false
    }

    if (!selectedSeller && !isPrivileged) {
      Swal.fire(
        'Permissão negada',
        'Sem vendedor selecionado, o filtro é permitido apenas para admin, gerente ou owner.',
        'error'
      )
      return false
    }

    return true
  }

  const openAuthorization = () => {
    if (!validateFilter()) {
      return
    }

    setIsPasswordModalOpen(true)
  }

  const applyFilter = async (credential: string) => {
    const actionType = selectedSeller
      ? 'conference_search_seller'
      : 'conference_search_all'

    const auth = await authorizeSellerQueueAction(
      actionType,
      credential,
      selectedSeller?.id
    )

    if (!auth.authorized) {
      Swal.fire('Permissão negada', auth.message || 'Credencial sem permissão.', 'error')
      return
    }

    setLoading(true)
    try {
      const payload = {
        startDate: toUtcStartDate(startDate),
        finalDate: toUtcEndDate(finalDate),
        ...(orderIdSearch.trim() ? { orderId: Number(orderIdSearch) } : {}),
        ...(orderExternalSearch.trim() ? { orderidExt: orderExternalSearch.trim() } : {}),
        ...(customerNameSearch.trim() ? { customerName: customerNameSearch.trim() } : {}),
        ...(selectedSeller?.cpf ? { cpfSeller: selectedSeller.cpf } : {}),
        ...(selectedSeller?.sellerCode ? { codeSeller: selectedSeller.sellerCode } : {}),
      }

      const responseOrders = await filterAdminQueueSellerOrders(payload)
      setCurrentPage(1)

      if (!selectedSeller) {
        setOrders(responseOrders)
        return
      }

      const selectedCpf = selectedSeller.cpf ? normalizeCpf(selectedSeller.cpf) : ''
      const selectedCode = selectedSeller.sellerCode ? normalizeCode(selectedSeller.sellerCode) : ''

      const filteredOrders = responseOrders.filter((order) => {
        const { cpf, code } = extractSellerFields(order)
        const orderCpf = cpf ? normalizeCpf(cpf) : ''
        const orderCode = code ? normalizeCode(code) : ''

        const cpfMatch = selectedCpf && orderCpf ? selectedCpf === orderCpf : false
        const codeMatch = selectedCode && orderCode ? selectedCode === orderCode : false

        return cpfMatch || codeMatch
      })

      setOrders(filteredOrders)
    } catch (error) {
      const message = typeof error === 'string' ? error : 'Erro ao filtrar pedidos da conferência.'
      Swal.fire('Erro', message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const getStatusLabel = (order: ConferenceOrderItem): string => {
    return order.status?.[0]?.name || '-'
  }

  const summary = useMemo(() => {
    let withCpf = 0
    let withCode = 0
    let totalAmountCents = 0

    for (const order of orders) {
      const fields = extractSellerFields(order)
      if (fields.cpf) withCpf += 1
      if (fields.code) withCode += 1
      if (typeof order.total === 'number' && !Number.isNaN(order.total)) {
        totalAmountCents += order.total
      }
    }

    return {
      total: orders.length,
      withCpf,
      withCode,
      totalAmountCents,
    }
  }, [orders])

  const totalPages = Math.max(1, Math.ceil(orders.length / pageSize))
  const pagedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return orders.slice(startIndex, startIndex + pageSize)
  }, [orders, currentPage])

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(1, prev - 1))
  const handleNextPage = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1))

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Filtros da Conferência</h3>
        </div>
        <div className='card-body'>
          <div className='mb-4'>
            {checkingTotvsIntegration ? (
              <div className='alert alert-secondary py-3 mb-0'>
                Verificando integração TOTVS...
              </div>
            ) : hasTotvsIntegration ? (
              <div className='alert alert-info py-3 mb-0'>
                Integração TOTVS detectada (plugintypeId=17 e plugintemplateId=23).
                A busca de pedidos TOTVS será habilitada nesta aba.
              </div>
            ) : (
              <div className='alert alert-light py-3 mb-0'>
                Nenhuma integração TOTVS detectada para esta loja.
              </div>
            )}
          </div>

          <div className='row g-3'>
            <div className='col-md-3'>
              <label className='form-label'>Data Início</label>
              <input
                type='date'
                className='form-control'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Data Final</label>
              <input
                type='date'
                className='form-control'
                value={finalDate}
                onChange={(e) => setFinalDate(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Pedido</label>
              <input
                type='number'
                min='1'
                className='form-control'
                value={orderIdSearch}
                onChange={(e) => setOrderIdSearch(e.target.value)}
                placeholder='Número do pedido'
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Pedido Externo</label>
              <input
                type='text'
                className='form-control'
                value={orderExternalSearch}
                onChange={(e) => setOrderExternalSearch(e.target.value)}
                placeholder='Código externo'
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Cliente</label>
              <input
                type='text'
                className='form-control'
                value={customerNameSearch}
                onChange={(e) => setCustomerNameSearch(e.target.value)}
                placeholder='Nome do cliente'
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Vendedor</label>
              <select
                className='form-select'
                value={selectedSellerId || ''}
                onChange={(e) =>
                  setSelectedSellerId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={isSellerProfile && sellers.length <= 1}
              >
                <option value=''>Todos os vendedores</option>
                {sellers.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.name}
                    {seller.sellerCode ? ` (${seller.sellerCode})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2 d-flex align-items-end'>
              <button className='btn btn-primary w-100' onClick={openAuthorization}>
                <i className='fas fa-search me-2'></i>
                Filtrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Pedidos para Conferência</h3>
        </div>
        <div className='card-body'>
          <div className='row g-3 mb-4'>
            <div className='col-md-3'>
              <div className='card bg-light-primary'>
                <div className='card-body py-4'>
                  <div className='fs-2 fw-bold text-primary'>{summary.total}</div>
                  <div className='fs-7 text-gray-700'>Total de pedidos</div>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='card bg-light-warning'>
                <div className='card-body py-4'>
                  <div className='fs-2 fw-bold text-warning'>
                    {formatOrderTotal(summary.totalAmountCents)}
                  </div>
                  <div className='fs-7 text-gray-700'>Valor total dos pedidos</div>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='card bg-light-success'>
                <div className='card-body py-4'>
                  <div className='fs-2 fw-bold text-success'>{summary.withCpf}</div>
                  <div className='fs-7 text-gray-700'>Pedidos com CPF do vendedor</div>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='card bg-light-info'>
                <div className='card-body py-4'>
                  <div className='fs-2 fw-bold text-info'>{summary.withCode}</div>
                  <div className='fs-7 text-gray-700'>Pedidos com código do vendedor</div>
                </div>
              </div>
            </div>
          </div>

          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Num. Pedido</th>
                  <th>Pedido Ext.</th>
                  <th>Cliente</th>
                  <th>Valor do Pedido</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>CPF Vendedor</th>
                  <th>Código Vendedor</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className='text-center py-5'>
                      Nenhum pedido encontrado para os filtros informados.
                    </td>
                  </tr>
                ) : (
                  pagedOrders.map((order) => {
                    const sellerFields = extractSellerFields(order)
                    return (
                      <tr key={order.id}>
                        <td className='fw-bold'>{order.id}</td>
                        <td>{order.orderidExt || '-'}</td>
                        <td>{order.customer?.name || '-'}</td>
                        <td>{formatOrderTotal(order.total)}</td>
                        <td>{new Date(order.created).toLocaleString('pt-BR')}</td>
                        <td>{getStatusLabel(order)}</td>
                        <td>{sellerFields.cpf || '-'}</td>
                        <td>{sellerFields.code || '-'}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {orders.length > 0 && (
            <div className='d-flex align-items-center justify-content-between mt-4'>
              <div className='text-muted fs-7'>
                Exibindo {(currentPage - 1) * pageSize + 1} a{' '}
                {Math.min(currentPage * pageSize, orders.length)} de {orders.length} pedidos
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                <span className='fw-semibold fs-7'>
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  className='btn btn-light btn-sm'
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PasswordConfirmModal
        isOpen={isPasswordModalOpen}
        title='Autorização para Conferência'
        description={
          selectedSeller
            ? 'Informe senha do vendedor selecionado ou de admin, gerente, owner ou administrador para filtrar.'
            : 'Informe senha de admin, gerente, owner ou administrador para filtrar todos os vendedores.'
        }
        onCancel={() => {
          setIsPasswordModalOpen(false)
        }}
        onConfirm={async (password) => {
          setIsPasswordModalOpen(false)
          await applyFilter(password)
        }}
      />
    </div>
  )
}
